<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useActivitiesStore } from '@/stores/useActivityStore'
import { usePartnerStore } from '@/stores/usePartnerStore'

import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import TimeRemainingCell from '@/components/common/feedback/TimeRemainingCell.vue'
import ActivityEdit from '@/components/features/activities/ActivityEdit.vue'
import PartnerButtonRedirect from '@/components/common/buttons/PartnerButtonRedirect.vue'

import type { Activity, Partner } from '@/api-swagger'
import ActivitiesGridActions from '@/components/features/activities/ActivitiesGridActions.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { useToast } from 'vue-toast-notification'
import DeleteConfirmation from '@/components/common/buttons/DeleteConfirmation.vue'

const activitiesStore = useActivitiesStore()
const partnerStore = usePartnerStore()

const { activities, loading, error } = storeToRefs(activitiesStore)
const $toast = useToast()

const activitiesWithPartnerData = ref<Activity[]>([])
const selectedActivity = ref<Activity | null>(null)

const selectedTodos = ref<Activity[]>([])

watch(
  activities,
  async (newActivities) => {
    if (newActivities && newActivities.length) {
      const uniquePartnerIds = [...new Set(newActivities.map((a) => a.resId.toString()))]
      const partners = await partnerStore.getPartnersByListId(uniquePartnerIds)

      if (partners) {
        const partnerMap = Object.fromEntries(partners.map((p: Partner) => [p.id, p]))

        activitiesWithPartnerData.value = newActivities.map((activity: Activity) => ({
          ...activity,
          partnerId: partnerMap[activity.resId] ?? null,
        }))
      }
    } else {
      activitiesWithPartnerData.value = []
    }
  },
  { immediate: true },
)

const selectActivity = (activity: Activity) => {
  selectedActivity.value = activity
}

const unselectActivity = () => {
  selectedActivity.value = null
}

const handleSelectionChanged = (selectedRows: Activity[]) => {
  selectedTodos.value = selectedRows
}

const bulkDelete = async () => {
  if (selectedTodos.value.length === 0) {
    $toast.warning('Aucune ligne sélectionnée')
    return
  }
  await activitiesStore.bulkDeleteActivities(selectedTodos.value.map((s) => s.id))
  selectedTodos.value = []
  $toast.success('Les lignes ont bien été supprimées.')
}
</script>

<template>
  <div class="p-6">
    <div class="flex flex-col p-2 gap-12">
      <Bs2Title title="Liste des tâches" />
    </div>
    <div v-if="(loading || !activities) && !error">
      <loader-center />
    </div>
    <div v-else-if="error">{{ error }}</div>
    <div v-else class="grid gap-4">
      <grid-vue
        @openModal="selectActivity"
        :list="activitiesWithPartnerData!"
        @onSelectionChanged="handleSelectionChanged"
        :rowSelection="{ mode: 'multiRow', selectAll: 'filtered' }"
        :cols="[
          { field: 'summary', headerName: 'Résumé', filter: true, cellClass: 'border-0' },
          {
            field: 'resName',
            headerName: 'Société',
            filter: true,
            cellClass: 'border-0',
            cellRenderer: PartnerButtonRedirect,
          },
          {
            field: 'dateDeadline',
            headerName: 'Date',
            filter: true,
            cellRenderer: TimeRemainingCell,
            cellRendererParams: (params: any) => {
              return { dateDeadline: params['dateDeadline'] }
            },
          },
          {
            field: 'actions',
            headerName: 'Actions',
            resizable: false,
            filter: false,
            minWidth: 250,
            maxWidth: 250,
            cellRenderer: ActivitiesGridActions,
            cellRendererParams: {
              onClick: (params: any) => {
                selectActivity(params)
              },
              tooltipPosition: 'left',
            },
            sortable: false,
            cellClass: 'border-0 grid-action ',
          },
        ]"
        :pagination-page-size="50"
      >
        <template v-slot:button>
          <delete-confirmation
            v-if="selectedTodos.length > 0"
            @delete="bulkDelete"
            :delete-text="`${selectedTodos.length} sélectionné(s)`"
            confirm-text="Supprimer définitivement"
            cancel-text="Annuler"
            :loading="loading"
          />
        </template>
      </grid-vue>
    </div>

    <div v-if="selectedActivity">
      <activity-edit
        :show-modal="true"
        :activity="selectedActivity"
        :user-id="selectedActivity.userId.id"
        @close="unselectActivity"
      />
    </div>
  </div>
</template>
